import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Home, 
  Euro, 
  CheckCircle2,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Lightbulb,
  PiggyBank
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CashFlowImmobilier() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Helmet>
        <title>Cash-Flow Immobilier : Définition, Calcul et Optimisation | Guide 2025</title>
        <meta name="description" content="Qu'est-ce que le cash-flow immobilier ? Comment le calculer et l'optimiser pour un investissement locatif qui s'autofinance. Guide complet avec exemples." />
        <meta name="keywords" content="cash flow immobilier, calcul cash flow locatif, autofinancement immobilier, investissement cash flow positif" />
        <link rel="canonical" href="https://achat-immo-gagnant.fr/guides/cash-flow-immobilier" />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Home className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-800">Achat Immo Gagnant</span>
          </Link>
          <Link to="/">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Calculer mon cash-flow
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Le Cash-Flow Immobilier
          </h1>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            L'indicateur clé pour savoir si votre investissement s'autofinance 
            ou vous coûte de l'argent chaque mois.
          </p>
        </div>
      </section>

      {/* Main */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Definition */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            Qu'est-ce que le cash-flow immobilier ?
          </h2>
          <div className="prose prose-lg text-slate-600">
            <p>
              Le <strong>cash-flow immobilier</strong> (ou flux de trésorerie) représente l'argent qui reste dans votre poche à la fin de chaque mois, après avoir payé toutes les charges liées à votre investissement locatif.
            </p>
            <p>
              C'est l'indicateur le plus important pour évaluer la viabilité d'un investissement immobilier. Un <strong>cash-flow positif</strong> signifie que le bien s'autofinance et vous rapporte de l'argent. Un <strong>cash-flow négatif</strong> signifie que vous devez mettre de l'argent de votre poche chaque mois.
            </p>
          </div>
          
          <div className="bg-purple-900 text-white rounded-xl p-8 mt-8 text-center">
            <p className="text-lg font-mono">
              Cash-flow = Loyers perçus - Toutes les charges - Mensualité de crédit
            </p>
          </div>
        </section>

        {/* Types */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-8">
            Les 3 types de cash-flow
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-green-50 rounded-xl p-6 border-2 border-green-300">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-green-800 mb-2">Cash-flow positif</h3>
              <p className="text-slate-600 mb-4">
                Le bien vous rapporte de l'argent chaque mois. Situation idéale !
              </p>
              <div className="text-2xl font-bold text-green-600">+ 150€/mois</div>
              <p className="text-sm text-slate-500 mt-2">Exemple : investissement autofinancé</p>
            </div>
            
            <div className="bg-amber-50 rounded-xl p-6 border-2 border-amber-300">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <PiggyBank className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-amber-800 mb-2">Cash-flow neutre</h3>
              <p className="text-slate-600 mb-4">
                Le bien s'autofinance mais ne rapporte rien. Équilibre parfait.
              </p>
              <div className="text-2xl font-bold text-amber-600">0€/mois</div>
              <p className="text-sm text-slate-500 mt-2">Le locataire paye votre crédit</p>
            </div>
            
            <div className="bg-red-50 rounded-xl p-6 border-2 border-red-300">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-red-800 mb-2">Cash-flow négatif</h3>
              <p className="text-slate-600 mb-4">
                Vous devez compléter chaque mois. Effort d'épargne requis.
              </p>
              <div className="text-2xl font-bold text-red-600">- 200€/mois</div>
              <p className="text-sm text-slate-500 mt-2">Typique des grandes villes</p>
            </div>
          </div>
        </section>

        {/* Calculation */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            Comment calculer le cash-flow ?
          </h2>
          
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 p-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">Exemple : Appartement T3 à 120 000€</h3>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Loyer mensuel</span>
                  <span className="font-semibold text-green-600">+ 800€</span>
                </div>
                
                <div className="text-sm font-medium text-slate-500 mt-4 mb-2">CHARGES :</div>
                
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Taxe foncière (mensuel)</span>
                  <span className="font-semibold text-red-600">- 100€</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Assurance PNO</span>
                  <span className="font-semibold text-red-600">- 20€</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Charges copro non récupérables</span>
                  <span className="font-semibold text-red-600">- 50€</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Provision entretien/vacance</span>
                  <span className="font-semibold text-red-600">- 50€</span>
                </div>
                
                <div className="text-sm font-medium text-slate-500 mt-4 mb-2">CRÉDIT :</div>
                
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Mensualité (120 000€ sur 20 ans à 3,5%)</span>
                  <span className="font-semibold text-red-600">- 695€</span>
                </div>
                
                <div className="flex items-center justify-between py-4 bg-purple-50 rounded-lg px-4 mt-4">
                  <span className="font-bold text-slate-800">CASH-FLOW MENSUEL</span>
                  <span className="font-bold text-red-600 text-xl">- 115€/mois</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-amber-50 rounded-xl p-4 mt-6 border border-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                <strong>Attention :</strong> Dans cet exemple, le cash-flow est négatif de 115€/mois. L'investisseur doit sortir 1 380€/an de sa poche. Ce n'est pas forcément un mauvais investissement si la plus-value attendue compense, mais il faut en avoir conscience.
              </p>
            </div>
          </div>
        </section>

        {/* Why it matters */}
        <section className="mb-16 bg-purple-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">
            Pourquoi le cash-flow est-il si important ?
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-800">Sécurité financière</h3>
                <p className="text-slate-600">Un cash-flow positif vous protège des imprévus (travaux, vacance locative, hausse des taux).</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-800">Capacité d'investissement</h3>
                <p className="text-slate-600">Un bien avec cash-flow neutre n'impacte pas votre taux d'endettement pour de futurs achats.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-800">Revenus complémentaires</h3>
                <p className="text-slate-600">Un portefeuille de biens avec cash-flow positif peut générer un revenu passif significatif.</p>
              </div>
            </div>
          </div>
        </section>

        {/* How to improve */}
        <section className="mb-16">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Lightbulb className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">
                Comment améliorer son cash-flow ?
              </h2>
              <p className="text-slate-600">Les leviers pour transformer un investissement déficitaire en machine à cash-flow</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">1. Allonger le crédit</h3>
              <p className="text-sm text-slate-600">Passer de 20 à 25 ans réduit la mensualité de ~15% sans changer le rendement final.</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">2. Négocier le taux</h3>
              <p className="text-sm text-slate-600">Faire jouer la concurrence entre banques peut faire gagner 0,3 à 0,5 points de taux.</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">3. Optimiser les loyers</h3>
              <p className="text-sm text-slate-600">Location meublée, colocation, Airbnb : des stratégies pour augmenter les revenus.</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">4. Réduire les charges</h3>
              <p className="text-sm text-slate-600">Travaux d'économie d'énergie, changement de syndic, gestion en direct...</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">5. Acheter moins cher</h3>
              <p className="text-sm text-slate-600">Négocier le prix d'achat ou viser les biens avec travaux à fort potentiel.</p>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">6. Investir en province</h3>
              <p className="text-sm text-slate-600">Les rendements sont souvent meilleurs hors des grandes métropoles.</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-2xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Calculez votre cash-flow en 5 minutes
          </h2>
          <p className="text-purple-100 mb-6 max-w-xl mx-auto">
            Notre simulateur calcule automatiquement votre cash-flow mensuel en tenant compte de toutes les charges.
          </p>
          <Link to="/">
            <Button size="lg" className="bg-white text-purple-600 hover:bg-purple-50 text-lg px-8">
              Simuler maintenant
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </section>

        {/* Related */}
        <section className="mt-16">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Guides connexes</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/guides/simulateur-rentabilite-locative" className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-semibold text-blue-600">Simulateur rentabilité locative</h3>
              <p className="text-sm text-slate-600">Le guide complet pour calculer votre rentabilité</p>
            </Link>
            <Link to="/guides/calcul-rentabilite-locative-emprunt" className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-semibold text-blue-600">Calcul avec emprunt</h3>
              <p className="text-sm text-slate-600">L'effet de levier pour maximiser votre rendement</p>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-16">
        <div className="max-w-6xl mx-auto px-4 text-center text-slate-500 text-sm">
          © 2025 Achat Immo Gagnant. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
}
